import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLifeRing, faPaperPlane, faRotate, faSpinner } from '@fortawesome/free-solid-svg-icons';
import './SupportTickets.css';
import 'react-toastify/dist/ReactToastify.css';
import baseURL from '../../url';

const CATEGORY_LABELS = {
    general: 'General',
    orders: 'Pedidos',
    payments: 'Pagos',
    shipping: 'Envios',
    technical: 'Tecnico',
};

const PRIORITY_LABELS = {
    low: 'Baja',
    medium: 'Media',
    high: 'Alta',
    urgent: 'Urgente',
};

const STATUS_LABELS = {
    open: 'Abierto',
    in_progress: 'En proceso',
    closed: 'Cerrado',
};

function formatDateTime(value) {
    if (!value) {
        return '';
    }

    const parsed = new Date(String(value).replace(' ', 'T'));
    if (Number.isNaN(parsed.getTime())) {
        return value;
    }

    return parsed.toLocaleString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

function getMessageAuthorLabel(message) {
    if ((message?.authorType || '') === 'platform') {
        return message?.authorName?.trim() || 'Soporte Tucatalogo';
    }

    return message?.authorName?.trim() || 'Dueno de tienda';
}

export default function SupportTicketsPanel() {
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [replying, setReplying] = useState(false);
    const [tickets, setTickets] = useState([]);
    const [stats, setStats] = useState({ open: 0, in_progress: 0, closed: 0, total: 0 });
    const [store, setStore] = useState(null);
    const [selectedTicketId, setSelectedTicketId] = useState(null);
    const [replyMessage, setReplyMessage] = useState('');
    const [categoryOptions, setCategoryOptions] = useState(['general', 'orders', 'payments', 'shipping', 'technical']);
    const [priorityOptions, setPriorityOptions] = useState(['low', 'medium', 'high', 'urgent']);
    const [newTicket, setNewTicket] = useState({
        subject: '',
        category: 'general',
        priority: 'medium',
        message: '',
    });

    const selectedTicket = tickets.find((ticket) => Number(ticket.idSupportTicket) === Number(selectedTicketId)) || null;
    const storeName = store?.storeName?.trim() || 'Tu tienda';
    const lastMessage = selectedTicket?.messages?.length ? selectedTicket.messages[selectedTicket.messages.length - 1] : null;

    const hydrateDashboard = (payload) => {
        const nextTickets = Array.isArray(payload?.tickets) ? payload.tickets : [];
        setTickets(nextTickets);
        setStats(payload?.stats || { open: 0, in_progress: 0, closed: 0, total: nextTickets.length });
        if (payload?.store) {
            setStore(payload.store);
        }
        if (Array.isArray(payload?.categoryOptions) && payload.categoryOptions.length > 0) {
            setCategoryOptions(payload.categoryOptions);
        }
        if (Array.isArray(payload?.priorityOptions) && payload.priorityOptions.length > 0) {
            setPriorityOptions(payload.priorityOptions);
        }

        setSelectedTicketId((currentId) => {
            if (nextTickets.some((ticket) => Number(ticket.idSupportTicket) === Number(currentId))) {
                return currentId;
            }

            return nextTickets[0]?.idSupportTicket || null;
        });
    };

    const loadTickets = async ({ showSpinner = true } = {}) => {
        try {
            if (showSpinner) {
                setLoading(true);
            }

            const response = await fetch(`${baseURL}/supportTicketsGet.php?ts=${Date.now()}`, {
                method: 'GET',
                cache: 'no-store',
            });
            const data = await response.json();
            if (!response.ok || data.error) {
                throw new Error(data.error || 'No se pudieron cargar los tickets.');
            }

            hydrateDashboard(data);
        } catch (error) {
            console.error('Error al cargar tickets:', error);
            toast.error(error.message || 'No se pudieron cargar los tickets.');
        } finally {
            if (showSpinner) {
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        loadTickets();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const submitNewTicket = async (event) => {
        event.preventDefault();

        if (newTicket.subject.trim() === '' || newTicket.message.trim() === '') {
            toast.error('Completa asunto y mensaje.');
            return;
        }

        try {
            setSubmitting(true);
            const response = await fetch(`${baseURL}/supportTicketPost.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(newTicket),
            });
            const data = await response.json();
            if (!response.ok || data.error) {
                throw new Error(data.error || 'No se pudo enviar el ticket.');
            }

            hydrateDashboard(data);
            setNewTicket({
                subject: '',
                category: 'general',
                priority: 'medium',
                message: '',
            });
            if (data.ticket?.idSupportTicket) {
                setSelectedTicketId(data.ticket.idSupportTicket);
            }
            setReplyMessage('');
            toast.success(data.mensaje || 'Ticket enviado.');
            if (data.warning) {
                toast.warn(data.warning);
            }
        } catch (error) {
            console.error('Error al crear ticket:', error);
            toast.error(error.message || 'No se pudo enviar el ticket.');
        } finally {
            setSubmitting(false);
        }
    };

    const submitReply = async (event) => {
        event.preventDefault();

        if (!selectedTicket) {
            toast.error('Selecciona un ticket primero.');
            return;
        }

        if (replyMessage.trim() === '') {
            toast.error('Escribe tu respuesta.');
            return;
        }

        try {
            setReplying(true);
            const response = await fetch(`${baseURL}/supportTicketPut.php`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    ticketId: selectedTicket.idSupportTicket,
                    replyMessage,
                }),
            });
            const data = await response.json();
            if (!response.ok || data.error) {
                throw new Error(data.error || 'No se pudo enviar la respuesta.');
            }

            hydrateDashboard(data);
            setReplyMessage('');
            if (data.ticket?.idSupportTicket) {
                setSelectedTicketId(data.ticket.idSupportTicket);
            }
            toast.success(data.mensaje || 'Respuesta enviada.');
            if (data.warning) {
                toast.warn(data.warning);
            }
        } catch (error) {
            console.error('Error al responder ticket:', error);
            toast.error(error.message || 'No se pudo responder el ticket.');
        } finally {
            setReplying(false);
        }
    };

    return (
        <section className='supportCenterPage'>

            <div className='supportCenterHero'>
                <div>
                    <span className='supportCenterEyebrow'>{storeName}</span>
                    <h2>Tickets de soporte</h2>
                    <p>Envía tus consultas y revisa el historial de cada conversación con soporte.</p>
                </div>
                <button type='button' className='supportCenterGhostButton' onClick={() => loadTickets()}>
                    <FontAwesomeIcon icon={faRotate} />
                    Actualizar
                </button>
            </div>

            <div className='supportCenterStats'>
                <article className='supportCenterStatCard stat-open'>
                    <span>Abiertos</span>
                    <strong>{Number(stats.open || 0)}</strong>
                    <p>Esperando respuesta de soporte.</p>
                </article>
                <article className='supportCenterStatCard stat-progress'>
                    <span>En proceso</span>
                    <strong>{Number(stats.in_progress || 0)}</strong>
                    <p>Conversaciones activas con soporte.</p>
                </article>
                <article className='supportCenterStatCard stat-closed'>
                    <span>Cerrados</span>
                    <strong>{Number(stats.closed || 0)}</strong>
                    <p>Conversaciones finalizadas.</p>
                </article>
            </div>

            <div className='supportCenterShell'>
                <aside className='supportCenterSidebar'>
                    <form className='supportComposerCard' onSubmit={submitNewTicket}>
                        <div className='supportComposerHeading'>
                            <div>
                                <span className='supportCenterEyebrow'>Nuevo ticket</span>
                                <h3>Escribir a soporte</h3>
                            </div>
                            <span className='supportComposerBadge'>
                                <FontAwesomeIcon icon={faLifeRing} />
                                {Number(stats.total || 0)} total
                            </span>
                        </div>

                        <label>
                            <span>Asunto</span>
                            <input
                                type='text'
                                value={newTicket.subject}
                                onChange={(event) => setNewTicket((prev) => ({ ...prev, subject: event.target.value }))}
                                placeholder='Ej: No puedo actualizar el catalogo'
                            />
                        </label>

                        <div className='supportComposerGrid'>
                            <label>
                                <span>Categoria</span>
                                <select
                                    value={newTicket.category}
                                    onChange={(event) => setNewTicket((prev) => ({ ...prev, category: event.target.value }))}
                                >
                                    {categoryOptions.map((option) => (
                                        <option key={option} value={option}>
                                            {CATEGORY_LABELS[option] || option}
                                        </option>
                                    ))}
                                </select>
                            </label>

                            <label>
                                <span>Prioridad</span>
                                <select
                                    value={newTicket.priority}
                                    onChange={(event) => setNewTicket((prev) => ({ ...prev, priority: event.target.value }))}
                                >
                                    {priorityOptions.map((option) => (
                                        <option key={option} value={option}>
                                            {PRIORITY_LABELS[option] || option}
                                        </option>
                                    ))}
                                </select>
                            </label>
                        </div>

                        <label>
                            <span>Mensaje</span>
                            <textarea
                                value={newTicket.message}
                                onChange={(event) => setNewTicket((prev) => ({ ...prev, message: event.target.value }))}
                                placeholder='Explica el problema, qué estabas haciendo y qué esperabas que ocurriera.'
                                rows='6'
                            />
                        </label>

                        <button type='submit' className='supportPrimaryButton' disabled={submitting}>
                            <FontAwesomeIcon icon={submitting ? faSpinner : faPaperPlane} spin={submitting} />
                            {submitting ? 'Enviando...' : 'Enviar ticket'}
                        </button>
                    </form>

                    <div className='supportListCard'>
                        <div className='supportListHeading'>
                            <h3>Tickets de soporte</h3>
                            <span>{Number(stats.total || 0)} registrados</span>
                        </div>

                        <div className='supportTicketList'>
                            {loading ? (
                                <div className='supportEmptyState'>Cargando tickets...</div>
                            ) : tickets.length === 0 ? (
                                <div className='supportEmptyState'>Todavia no has enviado tickets de soporte.</div>
                            ) : (
                                tickets.map((ticket) => (
                                    <button
                                        key={ticket.idSupportTicket}
                                        type='button'
                                        className={`supportTicketListItem ${Number(selectedTicketId) === Number(ticket.idSupportTicket) ? 'is-active' : ''}`}
                                        onClick={() => setSelectedTicketId(ticket.idSupportTicket)}
                                    >
                                        <div className='supportTicketListTop'>
                                            <strong>#{ticket.idSupportTicket} {ticket.subject}</strong>
                                            <span className={`supportStatusBadge status-${ticket.status}`}>{STATUS_LABELS[ticket.status] || ticket.status}</span>
                                        </div>
                                        <div className='supportTicketListMeta'>
                                            <span>{CATEGORY_LABELS[ticket.category] || ticket.category}</span>
                                            <span className={`supportPriorityBadge priority-${ticket.priority}`}>{PRIORITY_LABELS[ticket.priority] || ticket.priority}</span>
                                        </div>
                                        <div className='supportTicketListMeta'>
                                            <span>{formatDateTime(ticket.createdAt)}</span>
                                            <span>{ticket.messages?.length || 0} mensajes</span>
                                        </div>
                                    </button>
                                ))
                            )}
                        </div>
                    </div>
                </aside>

                <section className='supportTicketDetail'>
                    {loading ? (
                        <div className='supportTicketCard supportEmptyState'>Cargando detalle del ticket...</div>
                    ) : selectedTicket ? (
                        <article className='supportTicketCard'>
                            <div className='supportTicketTop'>
                                <div>
                                    <span className='supportCenterEyebrow'>Ticket #{selectedTicket.idSupportTicket}</span>
                                    <h3>{selectedTicket.subject}</h3>
                                </div>
                                <div className='supportTicketBadges'>
                                    <span className={`supportPriorityBadge priority-${selectedTicket.priority}`}>{PRIORITY_LABELS[selectedTicket.priority] || selectedTicket.priority}</span>
                                    <span className={`supportStatusBadge status-${selectedTicket.status}`}>{STATUS_LABELS[selectedTicket.status] || selectedTicket.status}</span>
                                </div>
                            </div>

                            <div className='supportTicketMeta'>
                                <span><strong>Categoria:</strong> {CATEGORY_LABELS[selectedTicket.category] || selectedTicket.category}</span>
                                <span><strong>Prioridad:</strong> {PRIORITY_LABELS[selectedTicket.priority] || selectedTicket.priority}</span>
                                <span><strong>Creado:</strong> {formatDateTime(selectedTicket.createdAt)}</span>
                            </div>

                            <div className='supportTimelineCard'>
                                <div className='supportTimelineHeading'>
                                    <div>
                                        <span className='supportCenterEyebrow'>Conversacion</span>
                                        <h4>Todo queda registrado en el ticket</h4>
                                    </div>
                                    {lastMessage ? (
                                        <small>Ultima actividad: {formatDateTime(lastMessage.createdAt)}</small>
                                    ) : null}
                                </div>

                                <div className='supportTimeline'>
                                    {(selectedTicket.messages || []).map((message) => (
                                        <article key={`${selectedTicket.idSupportTicket}-${message.idSupportTicketMessage}`} className={`supportBubble supportBubble-${message.authorType || 'store'}`}>
                                            <div className='supportBubbleTop'>
                                                <strong>{getMessageAuthorLabel(message)}</strong>
                                                <span>{formatDateTime(message.createdAt)}</span>
                                            </div>
                                            <p>{message.message}</p>
                                        </article>
                                    ))}
                                </div>
                            </div>

                            <form className='supportReplyForm' onSubmit={submitReply}>
                                <div className='supportReplyHeader'>
                                    <div>
                                        <span className='supportCenterEyebrow'>Responder</span>
                                        <h4>Enviar un nuevo mensaje a soporte</h4>
                                    </div>
                                </div>

                                <textarea
                                    value={replyMessage}
                                    onChange={(event) => setReplyMessage(event.target.value)}
                                    rows='6'
                                    placeholder='Comparte más contexto, adjunta pasos o confirma si el problema sigue ocurriendo.'
                                    disabled={selectedTicket.status === 'closed'}
                                />

                                {selectedTicket.status === 'closed' ? (
                                    <div className='supportClosedNotice'>
                                        Este ticket esta cerrado y ya no acepta respuestas desde la tienda.
                                    </div>
                                ) : null}

                                <button type='submit' className='supportPrimaryButton' disabled={replying || selectedTicket.status === 'closed'}>
                                    <FontAwesomeIcon icon={replying ? faSpinner : faPaperPlane} spin={replying} />
                                    {replying ? 'Enviando...' : 'Enviar respuesta'}
                                </button>
                            </form>
                        </article>
                    ) : (
                        <div className='supportTicketCard supportEmptyState'>Selecciona un ticket o crea uno nuevo para empezar.</div>
                    )}
                </section>
            </div>
        </section>
    );
}
